import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Box, Grid } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSDataGrid } from '../../../shared/components/table';
import { RMSTextField, RMSCheckbox } from '../../../shared/components/form-controls';
import { RMSButton } from '../../../shared/components/buttons';
import { RMSIconButton } from '../../../shared/components/buttons/index';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import {
  getDictionary,
  saveDictionary,
  deleteDictionary,
  addDictionaryValue,
  updateDictionaryValue,
} from 'reducers/DictionaryReducer';
import SectionLabel from './SectionLabel';
import dictionaryValueSchema from '../../schema/dictionaryValueSchema';
import {
  DEFAULT_DICTIONARY_PERMISSIONS,
  DEFAULT_VALUES,
  DICTIONARY_GRID_COLUMNS,
} from '../../constants/dictionaryValueFormConstants';

const DictionaryManagementDialog = ({ open, onClose, dictionary, title }) => {
  const dispatch = useDispatch();
  const cachedValues = useSelector((state) => state.dictionary[dictionary] || []);
  const permissions = useSelector(
    (state) => state.permissions?.dictionary?.[dictionary] || DEFAULT_DICTIONARY_PERMISSIONS
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [defaultDisabled, setDefaultDisabled] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const methods = useForm({
    resolver: CreateFormResolver(dictionaryValueSchema),
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const visibleRows = useMemo(() => rows.filter((row) => !row.IsDeleted), [rows]);
  const editingRowguid = useWatch({ control: methods.control, name: 'ROWGUID' });
  const isEditing = Boolean(editingRowguid);

  const loadValues = useCallback(async () => {
    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getDictionary(dictionary),
      onSuccess: (result) => setRows(result?.data || []),
    });
  }, [dictionary, dispatch]);

  useEffect(() => {
    if (!open) return;
    loadValues();
  }, [open, loadValues]);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingRow(null);
    methods.reset(DEFAULT_VALUES);
  }, [methods]);

  const requestFormClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        `You have unsaved changes on this ${title.toLowerCase()}. Are you sure you want to close without saving?`,
        () => handleFormClose(),
        'Unsaved Changes'
      );
      return;
    }

    handleFormClose();
  }, [isDirty, requestConfirm, handleFormClose, title]);

  const requestDialogClose = useCallback(() => {
    if (formOpen && isDirty) {
      requestConfirm(
        `You have unsaved changes on this ${title.toLowerCase()}. Are you sure you want to close without saving?`,
        () => {
          handleFormClose();
          onClose?.();
        },
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [formOpen, isDirty, requestConfirm, handleFormClose, onClose, title]);

  const handleAdd = useCallback(() => {
    const defaultExists = visibleRows.some((row) => row.IsDefault);
    setDefaultDisabled(defaultExists);
    setEditingRow(null);
    methods.reset(DEFAULT_VALUES);
    setFormOpen(true);
  }, [visibleRows, methods]);

  const handleEdit = useCallback(
    (row) => {
      const defaultRow = visibleRows.find((r) => r.IsDefault);
      setDefaultDisabled(Boolean(defaultRow && defaultRow.Code !== row.Code));
      setEditingRow(row);
      methods.reset({
        ROWGUID: row.ROWGUID,
        Code: row.Code || '',
        Description: row.Description || '',
        Category: row.Category || '',
        IsDefault: Boolean(row.IsDefault),
        IsActive: Boolean(row.IsActive),
      });
      setFormOpen(true);
    },
    [visibleRows, methods]
  );

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete "${row.Code}"?`,
        async () => {
          await executeApiAction({
            setLoading: setDeleting,
            dispatch,
            action: () => deleteDictionary(dictionary, row.ROWGUID),
            onSuccess: () => {
              dispatch(notify(`${title} deleted successfully`, 'success'));

              const remaining = cachedValues.filter((r) => r.ROWGUID !== row.ROWGUID);
              dispatch(updateDictionaryValue(remaining, dictionary));

              loadValues();
            },
          });
        },
        `Delete ${title}`
      );
    },
    [dispatch, requestConfirm, dictionary, title, cachedValues, loadValues]
  );

  const onSubmit = async (data) => {
    const payload = {
      ROWGUID: data.ROWGUID || null,
      Code: data.Code,
      Description: data.Description || null,
      Category: data.Category || null,
      IsDefault: data.IsDefault,
      IsActive: data.IsActive,
      IsDeleted: false,
    };

    await executeApiAction({
      setLoading: setSaving,
      dispatch,
      action: () => saveDictionary(dictionary, payload),
      onSuccess: (savedValue) => {
        dispatch(notify(`${title} ${isEditing ? 'updated' : 'added'} successfully`, 'success'));

        if (isEditing) {
          const updated = cachedValues.map((row) =>
            row.ROWGUID === payload.ROWGUID ? { ...row, ...payload } : row
          );
          dispatch(updateDictionaryValue(updated, dictionary));
        } else {
          dispatch(addDictionaryValue(savedValue || payload, dictionary));
        }

        handleFormClose();
        loadValues();
      },
    });
  };

  const handleSave = methods.handleSubmit(onSubmit);

  const renderRowActions = useCallback(
    (row) => (
      <>
        {permissions.Edit && (
          <RMSIconButton
            icon={<EditIcon fontSize="small" />}
            tooltip="Edit"
            onClick={() => handleEdit(row)}
          />
        )}
        {permissions.Delete && (
          <RMSIconButton
            className="delete-icon"
            icon={<DeleteIcon fontSize="small" />}
            tooltip="Delete"
            onClick={() => handleDelete(row)}
          />
        )}
      </>
    ),
    [permissions, handleEdit, handleDelete]
  );

  return (
    <RMSCommonDialog
      open={open}
      title={title}
      onClose={requestDialogClose}
      loading={loading || saving}
      maxWidth="md"
      fullWidth
      closeOnBackdropClick={false}
      className="city-management-dialog"
      saveButton={{ show: false }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}>
      {formOpen && (
        <Box className="rms-dialog-section">
          <SectionLabel>{isEditing ? `Edit ${title}` : `Add ${title}`}</SectionLabel>
          <FormProvider {...methods}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <RMSTextField
                  name="Code"
                  label="Code"
                  disabled={isEditing}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField
                  name="Description"
                  label="Description"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField name="Category" label="Category" inputProps={{ maxLength: 10 }} />
              </Grid>
              <Grid item xs={4}>
                <RMSCheckbox name="IsDefault" label="Is Default" disabled={defaultDisabled} />
              </Grid>
              <Grid item xs={4}>
                <RMSCheckbox name="IsActive" label="Is Active" />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              {isEditing ? (
                <RMSRecordHistoryButton
                  disabled={!editingRow?.Created}
                  created={editingRow?.Created}
                  createdBy={editingRow?.CreatedBy}
                  updated={editingRow?.Updated}
                  updatedBy={editingRow?.UpdatedBy}
                />
              ) : (
                <span />
              )}
              <Box display="flex">
                <RMSButton
                  label="Save"
                  variant="contained"
                  color="primary"
                  prefixIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                />
                <Box ml={1}>
                  <RMSButton
                    label="Close"
                    variant="outlined"
                    color="secondary"
                    onClick={requestFormClose}
                  />
                </Box>
              </Box>
            </Box>
          </FormProvider>
        </Box>
      )}

      <Box className="city-management-dialog__grid-wrap">
        <RMSDataGrid
          primaryKey="Code"
          columns={DICTIONARY_GRID_COLUMNS}
          rows={visibleRows}
          renderActions={renderRowActions}
          enableColumnResize
          enableColumnReorder
          toolbar={{
            showToolbar: true,
            searchable: true,
            actions: {
              showAddButton: permissions.Create,
              onAdd: handleAdd,
            },
          }}
        />
      </Box>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </RMSCommonDialog>
  );
};

export default DictionaryManagementDialog;
